import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import AdBanner from '../components/AdBanner';
import { Send, CheckCircle, Loader2, Mail, Phone, MapPin, Clock } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [contactInfo, setContactInfo] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await axios.get(`${API}/settings/contact-info/public`);
        setContactInfo(res.data);
      } catch (err) {
        console.error('Error fetching contact info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContactInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/settings/contact/message`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(t('common.failedToSendMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const hasContactInfo = contactInfo.email || contactInfo.phone || contactInfo.address || contactInfo.working_hours;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-800 animate-fade-in-up">
      <AdBanner position="top" />
      <Breadcrumb items={[{ label: t('contact.contactUs'), href: null }]} />
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('contact.contactUs')}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">{t('contact.howCanWeHelp')}</p>
        </div>
      </div>
      <div className="container mx-auto px-4 py-12">
        <div className={`max-w-${hasContactInfo ? '5xl' : '2xl'} mx-auto`}>
          <div className={`grid grid-cols-1 ${hasContactInfo ? 'lg:grid-cols-3' : ''} gap-8`}>
            {hasContactInfo && (
              <div className="lg:col-span-1">
                <div className="bg-zinc-900 rounded-xl p-8 border border-zinc-800 space-y-6">
                  <h2 className="text-xl font-bold text-white mb-4">{t('contact.contactInfo', 'Contact Information')}</h2>
                  {contactInfo.email && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">Email</p>
                        <a href={`mailto:${contactInfo.email}`} className="text-white hover:text-emerald-400 transition-colors">{contactInfo.email}</a>
                      </div>
                    </div>
                  )}
                  {contactInfo.phone && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">{t('contact.phone', 'Phone')}</p>
                        <a href={`tel:${contactInfo.phone}`} className="text-white hover:text-emerald-400 transition-colors">{contactInfo.phone}</a>
                      </div>
                    </div>
                  )}
                  {contactInfo.address && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">{t('contact.address', 'Address')}</p>
                        <p className="text-white">{contactInfo.address}</p>
                      </div>
                    </div>
                  )}
                  {contactInfo.working_hours && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">{t('contact.workingHours', 'Working Hours')}</p>
                        <p className="text-white">{contactInfo.working_hours}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={hasContactInfo ? 'lg:col-span-2' : ''}>
              <div className="bg-zinc-900 rounded-xl p-8 border border-zinc-800">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-4">{t('contact.messageSent')}</h2>
                    <p className="text-zinc-400 mb-6">{t('contact.messageSent')}</p>
                    <Button onClick={() => setSubmitted(false)} className="bg-emerald-600 hover:bg-emerald-700">{t('contact.send')}</Button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-white mb-6">{t('contact.getInTouch')}</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">{t('contact.name')}</Label>
                          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('auth.enterName')} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">{t('contact.email')}</Label>
                          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="john@example.com" required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('contact.subject')}</Label>
                        <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('contact.howCanWeHelp')} required />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('contact.message')}</Label>
                        <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white min-h-32" placeholder={t('contact.writeMessage')} required />
                      </div>
                      {error && <p className="text-red-400 text-sm">{error}</p>}
                      <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 py-6">
                        {submitting ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('common.saving')}</>) : (<><Send className="w-5 h-5 mr-2" />{t('contact.send')}</>)}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
